/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
      // No transitionend fires for a detached element, so release the header
      // demotion (notably the body class) immediately rather than leaving it
      // pinned by a timer on a node that is gone.
      this.finishClose();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      if (window._rlwSearch) {
        return this._getRelewiseResults(searchTerm);
      }

      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Gets results from Relewise and renders the stacked search dropdown.
     * @param {string} searchTerm - Search query.
     */
    async _getRelewiseResults(searchTerm) {
      if (!this._rlwSeq) this._rlwSeq = 0;
      const seq = ++this._rlwSeq;
      this.setLiveRegionLoadingState();

      try {
        const data = await window._rlwSearch.dropdown(searchTerm);
        if (seq !== this._rlwSeq) return;

        const html = window._rlwSearch.buildHtml(data, searchTerm);
        this.results.innerHTML = `<div class="rlw-ps">${html}</div>`;
        this.setAttribute('results', true);
        this.open();

        // The product rows painted before every shop-price lookup landed
        // (relewise.js caps the wait at 500ms): re-render them once, unless
        // the query has moved on or the dropdown closed (ClickUp 869f5wwgt).
        if (data?.rlwLateLookups && window._rlwSearch.refreshProductRows) {
          data.rlwLateLookups.then(() => {
            if (seq !== this._rlwSeq || !this.hasAttribute('open')) return;
            window._rlwSearch.refreshProductRows(this.results, data);
          });
        }
      } catch (e) {
        console.error('[Relewise search]', e);
        this.close();
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

  /**
   * Opens the results listbox.
   */
  open() {
    // Cancel a close that is still fading out, otherwise its deferred teardown
    // would strip [open] moments after this open() re-added it.
    this.cancelDeferredTeardown();

    // GA4: track closed -> open transitions only; re-renders while already
    // open call open() again and must not re-fire (s360 view_search_drawer).
    if (!this.hasAttribute('open')) {
      window.ilvaTrack?.('view_search_drawer', { search_term: this.getQuery() });
    }

    this.overlay.classList.add('is-visible');
    if (this.closest('.header__grid--ilva')) {
      this.overlay.classList.add('overlay--over-nav');
    }
    if (this.getQuery().length && this.resetBtn) this.resetBtn.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.removeAttribute('closing');
    this.setAttribute('open', '');
    document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      }
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      if (!this.hasAttribute('open') && !evt) return;

      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

    // Start the overlay fading, but keep the state that demotes the header
    // (announcement bar, search form, main menu) below it until the fade has
    // finished — dropping it now makes those elements jump above a still-visible
    // overlay for the length of the transition, which reads as a white blink.
    this.overlay.classList.remove('is-visible');
    if (this.resetBtn) this.resetBtn.hidden = true;
    this.input.setAttribute('aria-activedescendant', '');
    this.input.setAttribute('aria-expanded', 'false');

    // Swap [open] for [closing]: the panel hides immediately, the demotion
    // rules keep matching. Set before the listener so a 0s transition that
    // fires synchronously still sees a consistent state.
    this.setAttribute('closing', '');
    this.removeAttribute('open');

    this.deferTeardown();

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      if (this.resetBtn) {
        this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      }
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }

    /**
     * Drops the demotion state once the overlay fade-out has finished.
     * Uses transitionend where available, with a timeout fallback because it
     * does not fire when the transition is absent, zero-length, or interrupted
     * (e.g. prefers-reduced-motion, or the element being hidden mid-fade).
     */
    deferTeardown() {
      this.cancelDeferredTeardown();

      const duration = this.getOverlayFadeDuration();

      if (duration === 0) {
        this.finishClose();
        return;
      }

      this.teardownHandler = (evt) => {
        // Only the overlay's own opacity fade ends the close.
        if (evt.target !== this.overlay || evt.propertyName !== 'opacity') return;
        this.finishClose();
      };
      this.overlay.addEventListener('transitionend', this.teardownHandler);

      // Fallback: fires if transitionend never arrives. Padded so it loses the
      // race against a transition that does complete normally.
      this.teardownTimer = setTimeout(() => this.finishClose(), duration + 50);
    }

    /**
     * Reads the overlay's actual fade-out duration so the fallback stays in
     * sync with the stylesheet instead of hardcoding a second source of truth.
     * @returns {number} Duration in milliseconds.
     */
    getOverlayFadeDuration() {
      const styles = getComputedStyle(this.overlay);
      const toMs = (value) => {
        const raw = (value || '').split(',')[0].trim();
        if (!raw) return 0;
        return raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
      };
      const total = toMs(styles.transitionDuration) + toMs(styles.transitionDelay);
      return Number.isFinite(total) && total > 0 ? total : 0;
    }

    cancelDeferredTeardown() {
      if (this.teardownTimer) {
        clearTimeout(this.teardownTimer);
        this.teardownTimer = null;
      }
      if (this.teardownHandler) {
        this.overlay.removeEventListener('transitionend', this.teardownHandler);
        this.teardownHandler = null;
      }
    }

    /**
     * Final step of closing: releases the header demotion once the overlay is
     * no longer painted. Idempotent — transitionend and the fallback timer can
     * both land.
     */
    finishClose() {
      this.cancelDeferredTeardown();
      if (!this.hasAttribute('closing')) return;
      this.removeAttribute('closing');
      if (this.closest('.header__grid--ilva')) {
        this.overlay.classList.remove('overlay--over-nav');
      }
      document.body.classList.remove('overlay-predictive-search');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
